import axios from 'axios';

// 创建axios实例
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '/api',
  timeout: 180000,  // 180秒(3分钟)超时，原本是30秒
  // 不要在这里设置 Content-Type，让拦截器按需设置
  withCredentials: true,  // 允许跨域请求携带凭证
  maxRedirects: 5,       // 允许最多5次重定向
  validateStatus: status => status >= 200 && status < 300, // 只接受2xx的响应状态
  // 参数序列化
  paramsSerializer: params => {
    return Object.keys(params)
      .filter(key => params[key] !== undefined && params[key] !== null)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&');
  }
});

// 请求拦截器
axiosInstance.interceptors.request.use(
  config => {
    // 获取token
    const token = localStorage.getItem('token');
    
    if (token) {
      // 标准化token格式
      let cleanToken = token;
      if (!token.startsWith('Bearer ')) {
        cleanToken = `Bearer ${token.split(' ')[1]}`;
        localStorage.setItem('token', cleanToken);
      }
      config.headers['Authorization'] = cleanToken;
    }

    // 仅在非文件上传时设置Content-Type
    if (!config.headers['Content-Type'] && !/upload(-image)?|fetch-image/.test(config.url)) {
      config.headers['Content-Type'] = 'application/json';
    }

    // 移除URL末尾的斜杠
    if (config.url && config.url !== '/') {
      config.url = config.url.replace(/\/+$/, '');
    }

    return config;
  },
  error => Promise.reject(error)
);

// 响应拦截器
axiosInstance.interceptors.response.use(
  response => {
    const data = response.data;

    // 处理登录响应
    if (response.config.url.includes('/auth/login')) {
      if (data.code === 0 && data.data) {
        const { access_token, token_type, user } = data.data;
        if (!access_token || !token_type) {
          throw new Error('登录响应格式不正确');
        }
        // 保存token和用户信息
        const token = `${token_type} ${access_token}`;
        localStorage.setItem('token', token);
        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
        } else {
          // 如果登录响应中没有用户信息，立即获取用户信息
          axiosInstance.get('users/me').then(userResponse => {
            if (userResponse.data) {
              localStorage.setItem('user', JSON.stringify(userResponse.data));
            }
          }).catch(error => {
            console.error('获取用户信息失败:', error);
          });
        }
      }
      return data;
    }

    // 处理标准格式响应（带有code字段的）
    if (data && typeof data.code !== 'undefined') {
      // 处理特殊错误代码：伯索平台连接错误等网络错误
      if (data.code === -2 || data.code === -3) {
        // 返回完整响应，让业务代码处理网络错误
        return {
          code: data.code,
          message: data.message || data.msg || '伯索平台连接错误',
          data: data.data || { items: [], total: 0 }
        };
      }
      
      // 处理成功响应 - code为0或200都表示成功
      if (data.code === 0 || data.code === 200) {
        return {
          code: 0,
          message: data.message || 'success',
          data: data.data
        };
      }
      
      // 处理错误响应 - 只有当code不是0、200、-2、-3时才抛出错误
      throw new Error(data.message || data.msg || '请求失败');
    }

    // 处理分页列表格式响应（items和total的格式）
    if (data && Array.isArray(data.items) && typeof data.total !== 'undefined') {
      return {
        code: 0,
        message: 'success',
        data: {
          items: data.items,
          total: data.total,
          page: data.page,
          page_size: data.page_size
        }
      };
    }

    // 处理普通数组响应
    if (Array.isArray(data)) {
      return {
        code: 0,
        message: 'success',
        data: {
          items: data,
          total: data.length
        }
      };
    }

    // 处理普通对象响应
    return {
      code: 0,
      message: 'success',
      data: data
    };
  },
  error => {
    console.error('[axiosInstance] 响应错误:', error);
    if (error.response) {
      console.error('[axiosInstance] error.response:', error.response);
    }
    // 处理无响应错误：优先保留原始错误信息，避免被统一文案掩盖真实原因
    if (!error.response) {
      const originalMessage = error?.message || error?.toString?.() || '';
      throw new Error(originalMessage || '请求失败：未收到服务器响应');
    }

    // 处理HTTP错误
    const status = error.response.status;
    const data = error.response.data;

    // 处理401错误：清理登录态但不强制刷新页面，交由页面决定如何提示和跳转
    if (status === 401 && !error.config.url.includes('/auth/login')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      throw new Error(data?.message || data?.detail || '登录已过期，请重新登录');
    }

    // 如果后端返回了标准错误格式
    if (data && (data.code !== 0 && data.code !== 200)) {
      throw new Error(data.message || data.msg || `请求失败(${status})`);
    }

    // 其他错误情况
    throw new Error(data?.message || data?.msg || `请求失败(${status})`);
  }
);

export default axiosInstance; 